// src/stripeClient.js
// Client-side Stripe helpers — calls Netlify functions

import { auth } from './firebase';

const APP_URL = 'https://creatorcommandos.app';

export const PLANS = {
  free: {
    label: 'Free',
    channels: 1,
    credits: 25,
    price_monthly: 0,
    price_annual: 0,
  },
  solo: {
    label: 'Solo',
    channels: 1,
    credits: 100,
    price_monthly: 29,
    price_annual: 23,
    key_monthly: 'solo_monthly',
    key_annual: 'solo_annual',
  },
  pro: {
    label: 'Pro',
    channels: 2,
    credits: 250,
    price_monthly: 49,
    price_annual: 39,
    key_monthly: 'pro_monthly',
    key_annual: 'pro_annual',
  },
  agency: {
    label: 'Agency',
    channels: 10,
    credits: 1000,
    price_monthly: 99,
    price_annual: 79,
    key_monthly: 'agency_monthly',
    key_annual: 'agency_annual',
  },
};

async function getAuthHeader() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// Start Stripe checkout — redirects to Stripe
export async function startCheckout(planKey) {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in first');

  const resp = await fetch(`${APP_URL}/.netlify/functions/stripe-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      planKey,
      email: user.email,
      firebaseUid: user.uid,
    }),
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || 'Checkout failed');

  // Redirect to Stripe hosted checkout
  window.location.href = data.url;
}

// Open Stripe Customer Portal for subscription management
export async function openBillingPortal() {
  const authHeaders = await getAuthHeader();
  const resp = await fetch(`${APP_URL}/.netlify/functions/stripe-portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || 'Portal failed');

  window.location.href = data.url;
}

// Check if user has access to a feature based on their plan
export function canAccess(userRole, feature) {
  const status = userRole?.subscription_status;
  const plan = userRole?.plan || 'free';
  const isActive = status === 'active' || status === 'trialing';

  switch (feature) {
    case 'ai_credits':
      return (userRole?.ai_credits_remaining || 0) > 0;
    case 'team_access':
      return isActive && ['pro', 'agency'].includes(plan);
    case 'multi_channel':
      return isActive && ['pro', 'agency'].includes(plan);
    case 'comment_analysis':
      return isActive && ['pro', 'agency'].includes(plan);
    case 'csv_export':
      return isActive && plan === 'agency';
    default:
      return true;
  }
}

export function getPlanLimits(userRole) {
  const plan = userRole?.plan || 'free';
  return PLANS[plan] || PLANS.free;
}
